import { supabase } from '@/integrations/supabase/client';
import { TokenService } from '@/services/auth/TokenService';
import {
  MergeJob,
  MergeJobItem,
  MergeMinorProfile,
  MergePreviewRequest,
  MergePreviewResponse,
  MergeRequest,
} from '@/types/users/merge';

async function authHeaders() {
  const token = await TokenService.getAccessToken();
  if (!token) throw new Error('Session expired. Please refresh the page and try again.');
  return { Authorization: `Bearer ${token}` };
}

export class MergeUsersService {
  static async preview(request: MergePreviewRequest): Promise<MergePreviewResponse> {
    const { data, error } = await supabase.functions.invoke('handle-profile', {
      body: { mode: 'merge-preview', ...request },
      headers: await authHeaders(),
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to preview merge');
    return data;
  }

  static async merge(request: MergeRequest): Promise<{ success: boolean; jobId: number }> {
    const { data, error } = await supabase.functions.invoke('handle-profile', {
      body: { mode: 'merge', ...request },
      headers: await authHeaders(),
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to start merge');
    return data;
  }

  static async retry(jobId: number): Promise<{ success: boolean }> {
    const { data, error } = await supabase.functions.invoke('handle-profile', {
      body: { mode: 'merge-process-table', jobId, tableIndex: 0 },
      headers: await authHeaders(),
    });

    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Failed to retry merge');
    return data;
  }

  static async getJobStatus(jobId: number): Promise<MergeJob> {
    const { data, error } = await supabase
      .from('profile_merge_jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error) throw error;
    return data as MergeJob;
  }

  static async getJobItems(jobId: number): Promise<MergeJobItem[]> {
    const { data, error } = await supabase
      .from('profile_merge_job_items')
      .select('*')
      .eq('job_id', jobId)
      .order('id', { ascending: true });

    if (error) throw error;
    return (data || []) as MergeJobItem[];
  }

  static async getProfilesByIds(userIds: string[]): Promise<MergeMinorProfile[]> {
    if (userIds.length === 0) return [];

    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', userIds);

    if (error) throw error;
    return (data || []) as MergeMinorProfile[];
  }
}
